import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, SlidersHorizontal, Plus, Ship } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { revendaApi, type Cliente } from '@/api/endpoints/admin';
import { groupsApi } from '@/api/endpoints/groups';
import { usersApi } from '@/api/endpoints/users';
import { permissionsApi } from '@/api/endpoints/permissions';
import { Modal } from '@/features/admin/Modal';
import { ModuleEditor } from '@/features/admin/ModuleEditor';

const STATUS_CLS: Record<string, string> = {
  ativo: 'bg-status-running/10 text-status-running',
  suspenso: 'bg-status-off/15 text-status-off',
  inadimplente: 'bg-status-blocked/10 text-status-blocked',
};

export function ClientesPage() {
  const navigate = useNavigate();
  const clientesQ = useQuery({ queryKey: ['revenda', 'clientes'], queryFn: () => revendaApi.listClientes() });
  const myModsQ = useQuery({ queryKey: ['revenda', 'modules'], queryFn: () => revendaApi.myModules() });
  const clientes = clientesQ.data ?? [];

  const allowedKeys = useMemo(
    () => new Set((myModsQ.data ?? []).filter((m) => m.habilitado).map((m) => m.key)),
    [myModsQ.data],
  );

  const [modulesOf, setModulesOf] = useState<Cliente | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold">Meus Clientes</h1>
          <p className="text-sm text-muted-foreground">Cadastre clientes, libere módulos e adicione embarcações.</p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-1.5">
          <Plus className="h-4 w-4" /> Novo cliente
        </Button>
      </div>

      {clientesQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : clientesQ.error ? (
        <p className="text-sm text-destructive">{(clientesQ.error as Error).message}</p>
      ) : clientes.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Nenhum cliente ainda. Use “Novo cliente”.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {clientes.map((c) => (
            <Card key={c.id}>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-brand" />
                    <p className="truncate text-sm font-bold">{c.nome}</p>
                    <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', STATUS_CLS[c.status] ?? 'bg-secondary')}>{c.status}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{c.plano ? `${c.plano} · ` : ''}{c.device_limit || '∞'} disp. · {c.user_limit || '∞'} usuários</p>
                </div>
                <div className="flex gap-1.5">
                  {c.traccar_group_id ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => navigate(`/settings/devices/new?group=${c.traccar_group_id}&client=${encodeURIComponent(c.nome)}`)}
                    >
                      <Ship className="h-3.5 w-3.5" /> Dispositivo
                    </Button>
                  ) : null}
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setModulesOf(c)}>
                    <SlidersHorizontal className="h-3.5 w-3.5" /> Módulos
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {modulesOf && (
        <ClientModulesModal cliente={modulesOf} allowedKeys={allowedKeys} onClose={() => setModulesOf(null)} />
      )}
      {creating && <NovoClienteModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function NovoClienteModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [deviceLimit, setDeviceLimit] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const nm = nome.trim();
      // 1) grupo Traccar (entidade do cliente)
      const group = await groupsApi.create({ name: nm });
      // 2) usuário de acesso (opcional)
      let userId: number | null = null;
      if (email.trim() && senha.trim()) {
        const u = await usersApi.create({ name: nm, email: email.trim(), password: senha.trim() } as never);
        userId = u.id;
        if (group.id) await permissionsApi.link({ userId: u.id, groupId: group.id });
      }
      // 3) tenant NautMax (filho da revenda)
      await revendaApi.createCliente({
        nome: nm,
        traccar_user_id: userId,
        traccar_group_id: group.id ?? null,
        device_limit: deviceLimit ? Number(deviceLimit) : 0,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['revenda'] });
      onClose();
    },
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <Modal
      title="Novo cliente"
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={() => { setErr(null); if (!nome.trim()) { setErr('Nome é obrigatório.'); return; } create.mutate(); }}
            disabled={create.isPending}
          >
            {create.isPending ? 'Criando…' : 'Criar cliente'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <L label="Nome do cliente *">
          <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Marina Azul" />
        </L>
        <div className="rounded-md bg-secondary/40 p-2.5">
          <p className="mb-2 text-[11px] font-semibold text-muted-foreground">Acesso do cliente (opcional)</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <L label="E-mail de login">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@email.com" />
            </L>
            <L label="Senha">
              <Input type="password" value={senha} onChange={(e) => setSenha(e.target.value)} placeholder="senha inicial" />
            </L>
          </div>
          <p className="mt-1 text-[10px] text-muted-foreground">Se preencher, criamos o usuário de login já vinculado ao cliente.</p>
        </div>
        <L label="Limite de dispositivos (0 = sem limite definido)">
          <Input type="number" min={0} value={deviceLimit} onChange={(e) => setDeviceLimit(e.target.value)} placeholder="0" />
        </L>
        {err && <p className="text-xs font-medium text-status-blocked">{err}</p>}
        <p className="text-[10px] text-muted-foreground">
          Depois de criar, use o botão <strong>Dispositivo</strong> no cartão do cliente para cadastrar embarcações no grupo dele.
        </p>
      </div>
    </Modal>
  );
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function ClientModulesModal({
  cliente, allowedKeys, onClose,
}: {
  cliente: Cliente; allowedKeys: Set<string>; onClose: () => void;
}) {
  const qc = useQueryClient();
  // Reaproveita o catálogo da revenda como base e marca o estado atual do cliente.
  const q = useQuery({ queryKey: ['revenda', 'client-modules', cliente.id], queryFn: () => revendaApi.myModules() });
  const save = useMutation({
    mutationFn: (mods: { key: string; habilitado: boolean; cota: number | null }[]) =>
      revendaApi.setClientModules(cliente.id, mods),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['revenda'] }); onClose(); },
  });
  return (
    <Modal title={`Módulos — ${cliente.nome}`} onClose={onClose}>
      {q.isLoading ? <p className="text-sm text-muted-foreground">Carregando…</p> : q.data ? (
        <>
          {save.error && <p className="mb-2 text-xs text-destructive">{(save.error as Error).message}</p>}
          <ModuleEditor modules={q.data} allowedKeys={allowedKeys} saving={save.isPending} onSave={(d) => save.mutate(d)} />
        </>
      ) : <p className="text-sm text-destructive">Erro ao carregar.</p>}
    </Modal>
  );
}
